import { Box, CircularProgress, Stack } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import { WorkflowType } from '../../../interfaces/App';
import Button from '../Button/Button';

// How long to hold the boundary-step lock (see below). Comfortably covers a
// real accidental double-click (typically well under this) without making
// legitimate, deliberately-spaced navigation feel sluggish.
const BOUNDARY_LOCK_MS = 400;

interface WorkflowFooterProps {
  currentStep: number;
  isKnowledgeFilesLoading: boolean;
  isProcessing: boolean;
  steps: JSX.Element[];
  workflow: WorkflowType;
  isCurrentStepOptional?: boolean;
  isStepDirty?: boolean;
  navigateToAgents: () => void;
  previousStep: () => void;
  // When provided, the Next button (all steps except the last) becomes a
  // non-validating type="button" calling this handler directly instead of
  // submitting the form. Omit to keep the legacy validated-Next behavior
  // (used by callers other than the agent create/edit wizard).
  onNext?: () => void;
}

const WorkflowFooter: React.FC<WorkflowFooterProps> = ({
  currentStep,
  isKnowledgeFilesLoading,
  isProcessing,
  steps,
  workflow,
  isCurrentStepOptional = false,
  isStepDirty,
  navigateToAgents,
  previousStep,
  onNext,
}) => {
  const { t } = useTranslation();
  const submitButtonText =
    workflow === WorkflowType.CREATE ? t('Create') : t('Save');
  const isLastStep = currentStep === steps.length - 1;
  const isNonValidatingNext = !isLastStep && !!onNext;

  // Only the step immediately before the last is dangerous: clicking Next
  // there advances `currentStep` to the last step, which silently flips
  // *this same* DOM button node (React reuses it) from type="button"
  // (Next, with our onClick) to type="submit" (Create/Save, no onClick).
  // A fast accidental double-click can land its second click on that same
  // node after the flip and fire a real, unintended native form submit —
  // saving the agent before the user ever sees the last step. Every other
  // step keeps the button's role stable across a click, so there's
  // nothing to guard there.
  const isBoundaryStep = currentStep === steps.length - 2;
  const [isBoundaryLocked, setIsBoundaryLocked] = useState(false);
  const unlockTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(unlockTimeoutRef.current), []);

  const handleNextOrSubmitClick = () => {
    if (isBoundaryStep) {
      setIsBoundaryLocked(true);
      unlockTimeoutRef.current = setTimeout(
        () => setIsBoundaryLocked(false),
        BOUNDARY_LOCK_MS
      );
    }
    if (isNonValidatingNext) {
      onNext?.();
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        margin: '0 auto',
        maxWidth: '500px',
        padding: '1rem 2rem',
      }}
    >
      <Stack direction="row" gap="1rem" justifyContent="flex-end">
        {currentStep === 0 && (
          <Button
            type="button"
            onClick={navigateToAgents}
            disabled={isProcessing}
            variant="text"
            size="small"
            style={{ color: 'black' }}
          >
            {t('Back')}
          </Button>
        )}
        {currentStep > 0 && (
          <Button
            type="button"
            onClick={previousStep}
            disabled={isProcessing}
            variant="text"
            size="small"
            style={{ color: 'black' }}
          >
            {t('Back')}
          </Button>
        )}
        {isCurrentStepOptional && (
          <Button
            type="submit"
            disabled={isProcessing || isKnowledgeFilesLoading || !!isStepDirty}
            variant="text"
            size="small"
            style={{ color: 'black' }}
          >
            {t('Skip')}
          </Button>
        )}
        <Button
          type={isNonValidatingNext ? 'button' : 'submit'}
          onClick={handleNextOrSubmitClick}
          disabled={
            isBoundaryLocked ||
            isProcessing ||
            isKnowledgeFilesLoading ||
            (isCurrentStepOptional && !isStepDirty)
          }
          color="secondary"
          size="small"
          style={{ backgroundColor: '#1841ff', color: 'white' }}
        >
          {isLastStep && isProcessing ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} color="inherit" />
              <span>{submitButtonText}</span>
            </Stack>
          ) : isLastStep ? (
            submitButtonText
          ) : (
            t('Next')
          )}
        </Button>
      </Stack>
    </Box>
  );
};

export default WorkflowFooter;
